import { useState } from "react";
import {
  Box,
  ListSubheader,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  ALL_WORKSPACES_ID,
  IInsightsWorkspaceOption,
} from "v2/services/insights.service";
import {
  CONDUCCO_PRIMARY,
  INSIGHTS_BORDER,
  INSIGHTS_FONT,
  INSIGHTS_MUTED,
  INSIGHTS_TEXT,
} from "./tokens";
import {
  findWorkspace,
  groupWorkspacesByBuyer,
  workspaceLabel,
} from "./groupWorkspacesByBuyer";

export interface ContractSelectorProps {
  workspaces: IInsightsWorkspaceOption[];
  value: string;
  onChange: (workspaceId: string) => void;
  disabled?: boolean;
  /**
   * Oferece "Todos os contratos" (`ALL`). **Só o assistente**: ali o backend
   * resolve o escopo cruzado sem schema (`resolve-insights-scope.ts:27`, o
   * filtro vira `1=1`). Os relatórios do registry leem
   * `DBCONDUCCO."{workspace_id}"` e recusam `ALL` de propósito
   * (`infra/shared/sql.ts:46-53`) — não existe união de schemas.
   */
  allowAll?: boolean;
}

/**
 * O seletor de contrato, **um item por workspace**, agrupado por buyer.
 *
 * É o mesmo componente nas duas abas de propósito. Antes o assistente tinha um
 * seletor próprio que colapsava cada buyer em uma opção só, escondido dentro da
 * barra do campo de mensagem: com um buyer só ele virava texto sem menu, e não
 * havia como escolher contrato nenhum. Como mais da metade dos buyers tem dois
 * ou mais workspaces (produtos/operadoras diferentes, ambos ativos), o segundo
 * contrato era inalcançável no chat.
 */
export function ContractSelector({
  workspaces,
  value,
  onChange,
  disabled,
  allowAll,
}: ContractSelectorProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const groups = groupWorkspacesByBuyer(workspaces);
  const selected = findWorkspace(workspaces, value);
  const multiBuyer = groups.length > 1;
  // "Todos" só faz sentido havendo mais de um contrato para unir.
  const showAll = Boolean(allowAll) && workspaces.length > 1;
  const isAll = value === ALL_WORKSPACES_ID;

  const label = isAll
    ? "Todos os contratos"
    : selected
      ? workspaceLabel(selected)
      : "Selecione um contrato";

  // Um contrato só e sem opção de união: não há escolha a fazer, e um menu de
  // uma opção é ruído. O nome continua na tela — é ele que diz de onde sai o
  // dado.
  if (workspaces.length <= 1 && !showAll) {
    return (
      <Typography
        noWrap
        sx={{
          fontFamily: INSIGHTS_FONT,
          fontSize: "0.8125rem",
          fontWeight: 600,
          color: INSIGHTS_TEXT,
          maxWidth: 360,
        }}
      >
        {label}
      </Typography>
    );
  }

  const handleSelect = (workspaceId: string) => {
    onChange(workspaceId);
    setAnchorEl(null);
  };

  return (
    <>
      <Box
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-haspopup="listbox"
        aria-expanded={Boolean(anchorEl)}
        onClick={(e) => !disabled && setAnchorEl(e.currentTarget)}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setAnchorEl(e.currentTarget);
          }
        }}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          pl: 1.5,
          pr: 1,
          py: 0.625,
          borderRadius: "10px",
          border: `1px solid ${INSIGHTS_BORDER}`,
          backgroundColor: "#FFFFFF",
          cursor: disabled ? "default" : "pointer",
          opacity: disabled ? 0.5 : 1,
          maxWidth: 360,
          transition: "border-color 0.15s ease, background-color 0.15s ease",
          "&:hover": disabled
            ? undefined
            : {
                borderColor: CONDUCCO_PRIMARY,
                backgroundColor: "rgba(48,36,225,0.04)",
              },
          "&:focus-visible": {
            outline: `2px solid ${CONDUCCO_PRIMARY}`,
            outlineOffset: 2,
          },
        }}
      >
        <Typography
          noWrap
          sx={{
            fontFamily: INSIGHTS_FONT,
            fontSize: "0.8125rem",
            fontWeight: 600,
            color: INSIGHTS_TEXT,
          }}
        >
          {label}
        </Typography>
        <KeyboardArrowDownIcon
          sx={{ color: `${INSIGHTS_MUTED} !important`, fontSize: 18 }}
        />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        MenuListProps={{ dense: true }}
        PaperProps={{
          sx: {
            maxHeight: 420,
            borderRadius: "12px",
            border: `1px solid ${INSIGHTS_BORDER}`,
            boxShadow: "0 8px 24px rgba(26,26,46,0.12)",
          },
        }}
      >
        {showAll && (
          <MenuItem
            selected={isAll}
            onClick={() => handleSelect(ALL_WORKSPACES_ID)}
            sx={ITEM_SX}
          >
            Todos os contratos
          </MenuItem>
        )}
        {groups.flatMap((group) => [
          // Sem dois buyers não há o que separar — o cabeçalho seria decorativo.
          ...(multiBuyer
            ? [
                <ListSubheader
                  key={`header:${group.buyerOrganizationId}:${group.workspaces[0].id}`}
                  sx={{
                    fontFamily: INSIGHTS_FONT,
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    lineHeight: "28px",
                    textTransform: "uppercase",
                    color: INSIGHTS_MUTED,
                  }}
                >
                  {group.buyerLabel}
                </ListSubheader>,
              ]
            : []),
          ...group.workspaces.map((workspace) => (
            <MenuItem
              key={workspace.id}
              selected={workspace.id === value}
              onClick={() => handleSelect(workspace.id)}
              sx={ITEM_SX}
            >
              {workspaceLabel(workspace)}
            </MenuItem>
          )),
        ])}
      </Menu>
    </>
  );
}

const ITEM_SX = {
  fontFamily: INSIGHTS_FONT,
  fontSize: "0.8125rem",
  color: INSIGHTS_TEXT,
  borderRadius: "8px",
  mx: 0.5,
  "&.Mui-selected": {
    backgroundColor: "rgba(48,36,225,0.10)",
    color: CONDUCCO_PRIMARY,
    fontWeight: 600,
  },
};
